"use client";

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Star, Heart, Crown, Book, Church } from 'lucide-react';
import { AboutLayout } from '@/components/about/about-layout';

export default function LePapePage() {
  return (
    <AboutLayout activeSection="tarot">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Current Section Indicator */}
        <div className="mb-4 p-3 bg-purple-500/10 border border-purple-500/20 rounded-lg">
          <div className="flex items-center gap-2 text-purple-300">
            <Star className="h-4 w-4" />
            <span className="text-sm font-medium">🃏 Tarot</span>
            <span className="text-purple-500">/</span>
            <span className="text-sm">Marseille</span>
            <span className="text-purple-500">/</span>
            <span className="text-sm">Major Arkana</span>
            <span className="text-purple-500">/</span>
            <span className="text-sm">Le Pape</span>
          </div>
        </div>

        {/* Breadcrumb */}
        <div className="mb-6">
          <Link
            href="/about/tarot/systems/marseille/major-arcana"
            className="inline-flex items-center text-slate-400 hover:text-slate-200 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Major Arkana'ya Geri Dön
          </Link>
        </div>

        {/* Header with Card */}
        <div className="bg-gradient-to-r from-purple-900/20 to-indigo-900/20 rounded-xl p-8 mb-8 border border-purple-500/20">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
            <div className="lg:col-span-1 flex justify-center">
              <div className="w-48 h-72 rounded-xl overflow-hidden shadow-2xl">
                <img
                  src="/images/tarot/cards/marseille-le-pape.svg"
                  alt="Le Pape Tarot Card"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    const target = e.currentTarget;
                    target.style.display = 'none';
                    if (target.nextElementSibling) {
                      (target.nextElementSibling as HTMLElement).style.display = 'flex';
                    }
                  }}
                />
                <div className="hidden w-full h-full bg-gradient-to-b from-purple-900/40 to-indigo-900/40 border border-purple-500/30 flex-col items-center justify-center text-purple-300">
                  <span className="text-3xl font-bold mb-2">V</span>
                  <span className="text-lg font-semibold">LE PAPE</span>
                  <span className="text-sm text-center mt-2">The Hierophant</span>
                </div>
              </div>
            </div>
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-700 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-lg">V</span>
                </div>
                <div>
                  <h1 className="text-4xl font-bold text-slate-100">Le Pape</h1>
                  <p className="text-xl text-purple-400">Hierofant / Öğretim ve Gelenek</p>
                </div>
              </div>
              <p className="text-lg text-slate-200 leading-relaxed mb-4">
                Marseille geleneğinde beşinci numara. Ruhani öğretmen, gelenek koruyucusu ve
                bilgelik aktarıcısı rolündedir. Kurumsal öğretim ve manevi rehberliği temsil eder.
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-purple-500/20 text-purple-300 rounded-full text-sm">Öğretim</span>
                <span className="px-3 py-1 bg-purple-500/20 text-purple-300 rounded-full text-sm">Gelenek</span>
                <span className="px-3 py-1 bg-purple-500/20 text-purple-300 rounded-full text-sm">Rehberlik</span>
                <span className="px-3 py-1 bg-purple-500/20 text-purple-300 rounded-full text-sm">Bilgelik</span>
              </div>
            </div>
          </div>
        </div>

        {/* Card Meanings */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Positive Meanings */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h2 className="text-2xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
              <Book className="h-6 w-6 text-green-400" />
              Düz Pozisyon Anlamları
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Ruhani Öğretim</h3>
                  <p className="text-slate-400 text-sm">Bilgi aktarımı, öğretmenlik ve rehberlik.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Gelenek ve Kurallar</h3>
                  <p className="text-slate-400 text-sm">Kurumsal yapılar, geleneksel değerler.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Manevi Rehberlik</h3>
                  <p className="text-slate-400 text-sm">Ruhsal danışmanlık ve yol gösterme.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Toplumsal Konformizm</h3>
                  <p className="text-slate-400 text-sm">Sosyal normlara uyum, kabul görmüş yollar.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Negative Meanings */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h2 className="text-2xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
              <Church className="h-6 w-6 text-red-400" />
              Ters Pozisyon Anlamları
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Dogmatizm</h3>
                  <p className="text-slate-400 text-sm">Aşırı katı kurallar, esnek olmama.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Sahte Öğretmen</h3>
                  <p className="text-slate-400 text-sm">Yanıltıcı rehberlik, kötü niyetli öğretim.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Nonkonformizm</h3>
                  <p className="text-slate-400 text-sm">Kurallara başkaldırı, gelenekleri reddetme.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Otoriteye İsyan</h3>
                  <p className="text-slate-400 text-sm">Kurumsal yapıları reddedme, bağımsızlık arayışı.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Symbolism */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Marseille Sembolizmi</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <Crown className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Papalık Tacı</h3>
              <p className="text-slate-400 text-sm">Ruhani otorite ve üç dünya bilgisi.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">⚱️</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Asa/Sceptre</h3>
              <p className="text-slate-400 text-sm">Ruhani güç ve öğretim otoritesi.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-yellow-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">🔑</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Anahtarlar</h3>
              <p className="text-slate-400 text-sm">Gizli bilgilere erişim ve çözüm.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">👥</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">İki Mürit</h3>
              <p className="text-slate-400 text-sm">Öğrenci-öğretmen ilişkisi.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <Book className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Kutsal Kitap</h3>
              <p className="text-slate-400 text-sm">Yazılı öğreti ve geleneksel bilgi.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-indigo-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <Church className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Taht/Kürsü</h3>
              <p className="text-slate-400 text-sm">Öğretim makamı ve saygınlık.</p>
            </div>
          </div>
        </div>

        {/* Life Areas */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Yaşam Alanlarındaki Anlamlar</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Heart className="h-5 w-5 text-pink-400" />
                Aşk ve İlişkiler
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Geleneksel ilişki değerleri</li>
                <li>• Evlilik ve bağlılık</li>
                <li>• Ailevi onay</li>
                <li>• Ruhani bağ</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Star className="h-5 w-5 text-yellow-400" />
                Kariyer ve İş
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Öğretmenlik mesleği</li>
                <li>• Danışmanlık hizmetleri</li>
                <li>• Kurumsal pozisyonlar</li>
                <li>• Eğitim sektörü</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Book className="h-5 w-5 text-blue-400" />
                Kişisel Gelişim
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Formal eğitim alma</li>
                <li>• Mentor bulma</li>
                <li>• Disiplinli çalışma</li>
                <li>• Geleneksel yöntemler</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Church className="h-5 w-5 text-purple-400" />
                Manevi Gelişim
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Ruhani rehber arayışı</li>
                <li>• Geleneksel öğretiler</li>
                <li>• Toplu ibadet</li>
                <li>• Kutsal kitap çalışması</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Link
            href="/about/tarot/systems/marseille/major-arcana/l-empereur"
            className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-slate-300 hover:text-slate-100 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Önceki: L'Empereur
          </Link>

          <Link
            href="/about/tarot/systems/marseille/major-arcana/l-amoureux"
            className="inline-flex items-center px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
          >
            Sonraki: L'Amoureux
            <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
          </Link>
        </div>
      </div>
    </AboutLayout>
  );
}